import * as React from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { antGrey } from '@/components/styles/globalStyles';

export interface DragIndexState {
    active: UniqueIdentifier;
    over: UniqueIdentifier | undefined;
    direction?: 'left' | 'right';
    dragType?: 'column' | 'row';
}

export const DragIndexContext = React.createContext<DragIndexState>({ active: -1, over: -1 });

const dragActiveStyle = (dragState: DragIndexState, id: string, axis: 'column' | 'row'): React.CSSProperties => {
    const { active, over, dragType } = dragState;
    if (dragType !== axis) return {};
    if (active && active === id) {
        return { backgroundColor: antGrey.grey4 };
    }
    if (over && id === over && active !== over) {
        return { borderInlineStart: `1px dashed ${antGrey.grey6}` };
    }
    return {};
};

const MIN_ROW_HEIGHT = 24;

interface RowResizeHandleProps {
    onResize: (height?: number, allRows?: boolean) => void;
}

// the guide line is plain DOM so pointer moves never re-render the table; height commits once on release
const RowResizeHandle: React.FC<RowResizeHandleProps> = ({ onResize }) => {
    const guideRef = React.useRef<HTMLDivElement>();
    const isResizing = () => document.body.classList.contains('summary-resizing');

    const hideGuide = React.useCallback(() => {
        guideRef.current?.remove();
        guideRef.current = undefined;
        document.removeEventListener('scroll', hideGuide, true);
    }, []);
    const showGuide = (cell: HTMLTableCellElement) => {
        hideGuide();
        const cellRect = cell.getBoundingClientRect();
        const tableRect = cell.closest('table')!.getBoundingClientRect();
        const bounds = cell.closest('.summary-table-scroll')?.getBoundingClientRect() ?? tableRect;
        const left = Math.max(tableRect.left, bounds.left);
        const guide = document.createElement('div');
        guide.className = 'summary-resize-guide';
        Object.assign(guide.style, {
            left: `${left}px`,
            width: `${Math.min(tableRect.right, bounds.right) - left}px`,
            top: `${cellRect.bottom}px`,
        });
        document.body.appendChild(guide);
        guideRef.current = guide;
        return guide;
    };
    React.useEffect(() => hideGuide, [hideGuide]);

    return (
        <span
            aria-hidden
            className="summary-resize-handle"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => {
                e.stopPropagation();
                onResize(undefined, e.altKey || e.shiftKey);
            }}
            onPointerEnter={(e) => {
                if (isResizing()) return;
                showGuide(e.currentTarget.parentElement as HTMLTableCellElement);
                // no pointer events arrive while the page scrolls, so the hover line would outlive the row's position
                document.addEventListener('scroll', hideGuide, { capture: true, passive: true });
            }}
            onPointerLeave={() => {
                // pointer capture reports leaving the handle mid-drag too
                if (!isResizing()) hideGuide();
            }}
            onPointerDown={(e) => {
                if (e.button !== 0) return;
                // keep dnd-kit's PointerSensor on the parent <tr> from starting a drag
                e.stopPropagation();
                e.preventDefault();
                const handle = e.currentTarget;
                const cell = handle.parentElement as HTMLTableCellElement;
                const startHeight = cell.offsetHeight;
                const edge = cell.getBoundingClientRect().bottom;
                const startY = e.clientY;
                const guide = showGuide(cell);
                document.body.classList.add('summary-resizing');

                const heightAt = (ev: PointerEvent) => Math.max(MIN_ROW_HEIGHT, startHeight + ev.clientY - startY);
                const onMove = (ev: PointerEvent) => {
                    guide.style.top = `${edge + heightAt(ev) - startHeight}px`;
                };
                const onKeyDown = (ev: KeyboardEvent) => {
                    if (ev.key === 'Escape') cleanup();
                };
                const cleanup = () => {
                    hideGuide();
                    document.body.classList.remove('summary-resizing');
                    handle.removeEventListener('pointermove', onMove);
                    handle.removeEventListener('pointerup', onUp);
                    handle.removeEventListener('lostpointercapture', cleanup);
                    document.removeEventListener('keydown', onKeyDown);
                };
                const onUp = (ev: PointerEvent) => {
                    cleanup();
                    // Shift doubles for Alt, which some window managers grab for window drags
                    if (ev.clientY !== startY) onResize(heightAt(ev), ev.altKey || ev.shiftKey);
                };
                handle.setPointerCapture(e.pointerId);
                handle.addEventListener('pointermove', onMove);
                handle.addEventListener('pointerup', onUp);
                // also fires if the cell unmounts mid-drag
                handle.addEventListener('lostpointercapture', cleanup);
                document.addEventListener('keydown', onKeyDown);
            }}
        />
    );
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
    id: string;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
    id: string;
    onRowResize?: (height?: number, allRows?: boolean) => void;
}

export const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
    const dragState = React.useContext(DragIndexContext);
    const isDraggable = !!props.id;
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: props.id ?? '',
        disabled: !isDraggable,
        data: { type: 'column' },
    });

    if (!isDraggable) {
        return <th {...props} />;
    }

    const style: React.CSSProperties = {
        ...props.style,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
        ...dragActiveStyle(dragState, props.id, 'column'),
    };
    return <th {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

export const TableBodyCell: React.FC<BodyCellProps> = ({ onRowResize, children, ...props }) => {
    const dragState = React.useContext<DragIndexState>(DragIndexContext);
    return (
        <td {...props} style={{ ...props.style, ...dragActiveStyle(dragState, props.id, 'column') }}>
            <div className="summary-cell-content">{children}</div>
            {onRowResize ?
                <RowResizeHandle onResize={onRowResize} />
            :   null}
        </td>
    );
};

interface BodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

export const TableBodyRow: React.FC<BodyRowProps> = (props) => {
    const dragState = React.useContext(DragIndexContext);
    const rowKey = props['data-row-key'];
    const isDraggable = !!rowKey;
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: rowKey ?? '',
        disabled: !isDraggable,
        data: { type: 'row' },
    });

    if (!isDraggable) {
        return <tr {...props} />;
    }

    const style: React.CSSProperties = {
        ...props.style,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
        ...dragActiveStyle(dragState, rowKey, 'row'),
    };
    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};
